/**
 * time-zone — strefy czasowe i zamiana „daty z godziną” między momentem w czasie a zegarem.
 *
 * Dwie role strefy (zasada → FRONTEND.md §3.5):
 *  - **strefa użytkownika** (przeglądarki) — w niej człowiek wpisuje i widzi datę z godziną na
 *    karcie, liście i w kalendarzu; panel podaje ją sam (`apps/admin/src/lib/user-date-time.ts`),
 *  - **strefa tenanta** (ustawienie `tenant_settings.time_zone`, domyślnie `DEFAULT_TIME_ZONE`) —
 *    w niej liczy logika serwera: numeracja, data dokumentu „dziś”, termin w mailu, okno KSeF.
 *
 * Wartość „data z godziną” (kolumna `TIMESTAMPTZ`) to moment w czasie: w bazie i w API zawsze
 * ze strefą (ISO z `Z` albo przesunięciem). Zamiana na zegar i z powrotem idzie WYŁĄCZNIE przez
 * funkcje z tego pliku, zawsze z jawnie podaną strefą.
 */

/**
 * Strefa domyślna (IANA) — `Europe/Warsaw`: tenant bez własnego ustawienia i wszystko, co nie ma
 * kontekstu tenanta. Rynek jest polski, więc to też wartość startowa ustawienia tenanta.
 */
export const DEFAULT_TIME_ZONE = 'Europe/Warsaw';

/** Wartość pola daty bez godziny: `YYYY-MM-DD`. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Data z godziną BEZ strefy (wartość kontrolki `datetime-local` albo dawny zapis bez strefy). */
const NAIVE_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

/** Czy tekst to strefa czasowa IANA znana środowisku (np. `Europe/Warsaw`, `UTC`). */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.trim() === '' || timeZone.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

function wallClockFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = wallClockFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    wallClockFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/** Zegar (rok, miesiąc, dzień, godzina…) w podanej strefie dla danego momentu. */
export function wallClockIn(
  instant: Date,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts: Record<string, number> = {};
  for (const part of wallClockFormatter(timeZone).formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year ?? 1970,
    month: parts.month ?? 1,
    day: parts.day ?? 1,
    hour: parts.hour ?? 0,
    minute: parts.minute ?? 0,
    second: parts.second ?? 0,
  };
}

/** Przesunięcie strefy względem UTC (w minutach) w danym momencie, np. 60 zimą w Polsce. */
function offsetMinutes(epochMs: number, timeZone: string): number {
  const wall = wallClockIn(new Date(epochMs), timeZone);
  const wallAsUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );
  const wholeSeconds = Math.floor(epochMs / 1000) * 1000;
  return Math.round((wallAsUtc - wholeSeconds) / 60_000);
}

/**
 * Moment w czasie dla godziny „na zegarku” w podanej strefie. Zmiana czasu:
 *  - godzina powtórzona (jesienią) → wcześniejszy moment (jeszcze czas letni),
 *  - godzina nieistniejąca (wiosną) → przesunięta do przodu o długość zmiany (2:30 → 3:30),
 *    tak jak robią to przeglądarki.
 */
function wallClockToEpoch(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): number {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const DAY_MS = 86_400_000;
  const offsets = new Set([
    offsetMinutes(wallAsUtc - DAY_MS, timeZone),
    offsetMinutes(wallAsUtc, timeZone),
    offsetMinutes(wallAsUtc + DAY_MS, timeZone),
  ]);
  const valid = [...offsets]
    .map((offset) => wallAsUtc - offset * 60_000)
    .filter((epoch) => offsetMinutes(epoch, timeZone) * 60_000 === wallAsUtc - epoch)
    .sort((a, b) => a - b);
  if (valid.length > 0) return valid[0]!;
  // Godzina nieistniejąca — liczymy z przesunięciem sprzed zmiany czasu.
  return wallAsUtc - offsetMinutes(wallAsUtc - DAY_MS, timeZone) * 60_000;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Moment w czasie z wartości pola „data z godziną”: tekst ze strefą (`…Z`, `…+01:00`),
 * `Date`, albo tekst BEZ strefy — ten czytamy jako zegar podanej strefy. `null`, gdy wartość
 * pusta albo nieczytelna (sama data bez godziny też nie jest momentem).
 */
export function parseDateTime(value: unknown, timeZone: string): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  const naive = NAIVE_DATE_TIME.exec(text);
  if (naive) {
    const [, y, mo, d, h, mi, s] = naive;
    return new Date(
      wallClockToEpoch(
        timeZone,
        Number(y),
        Number(mo),
        Number(d),
        Number(h),
        Number(mi),
        Number(s ?? 0),
      ),
    );
  }
  if (DATE_ONLY.test(text)) return null;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Wartość pola „data z godziną” → wartość kontrolki `datetime-local` (`YYYY-MM-DDTHH:mm`)
 * na zegarze podanej strefy. Pusty tekst, gdy brak wartości.
 */
export function toDateTimeInput(value: unknown, timeZone: string): string {
  const instant = parseDateTime(value, timeZone);
  if (!instant) return '';
  const w = wallClockIn(instant, timeZone);
  return `${w.year}-${pad2(w.month)}-${pad2(w.day)}T${pad2(w.hour)}:${pad2(w.minute)}`;
}

/**
 * Wartość kontrolki `datetime-local` (godzina wpisana na zegarze podanej strefy) → moment
 * w czasie jako ISO w UTC (`…Z`) — tak wysyłamy do API. `null`, gdy pole puste albo nieczytelne.
 */
export function fromDateTimeInput(
  local: string | null | undefined,
  timeZone: string,
): string | null {
  if (!local) return null;
  const match = NAIVE_DATE_TIME.exec(local.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi] = match;
  return new Date(
    wallClockToEpoch(timeZone, Number(y), Number(mo), Number(d), Number(h), Number(mi)),
  ).toISOString();
}

/**
 * Data z godziną do wyświetlenia (np. „5.11.2026, 10:00:00” dla `pl-PL`) na zegarze podanej
 * strefy. Nieczytelna wartość wraca jako tekst, pusta — jako pusty tekst.
 */
export function formatDateTime(
  value: unknown,
  timeZone: string,
  locale = 'pl-PL',
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (value === null || value === undefined || value === '') return '';
  const instant = parseDateTime(value, timeZone);
  if (!instant) return String(value);
  return instant.toLocaleString(locale, { ...options, timeZone });
}

/**
 * Dzień (`YYYY-MM-DD`) w kalendarzu podanej strefy: data bez godziny wraca bez zmian,
 * moment w czasie — dzień, w którym wypada na zegarze tej strefy (5.11 0:30 w Polsce to 5.11,
 * choć w UTC to jeszcze 4.11).
 */
export function toDateKey(value: unknown, timeZone: string): string | null {
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) return value.trim();
  const instant = parseDateTime(value, timeZone);
  if (!instant) return null;
  const w = wallClockIn(instant, timeZone);
  return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}

/** Początek dnia `YYYY-MM-DD` (północ w podanej strefie) jako moment w czasie. */
export function dayStartIn(dateOnly: string, timeZone: string): Date {
  const match = DATE_ONLY.exec(dateOnly);
  if (!match) return new Date(Number.NaN);
  return new Date(
    wallClockToEpoch(timeZone, Number(match[1]), Number(match[2]), Number(match[3]), 0, 0),
  );
}

/** Początek dnia następnego po `YYYY-MM-DD` (północ w podanej strefie) — koniec zakresu `[start, next)`. */
export function nextDayStartIn(dateOnly: string, timeZone: string): Date {
  const match = DATE_ONLY.exec(dateOnly);
  if (!match) return new Date(Number.NaN);
  const next = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return new Date(
    wallClockToEpoch(
      timeZone,
      next.getUTCFullYear(),
      next.getUTCMonth() + 1,
      next.getUTCDate(),
      0,
      0,
    ),
  );
}

// ── Ustawienie strefy tenanta (`GET/PUT /api/settings/time-zone`) ───────────────

/** Kod uprawnienia do zmiany strefy tenanta (odczyt ma każdy zalogowany użytkownik tenanta). */
export const TIME_ZONE_MANAGE_PERMISSION = 'crm_core.time_zone.manage';

/** `GET /api/settings/time-zone` — strefa tenanta i czy osoba może ją zmienić. */
export interface TenantTimeZoneSettingResponse {
  timeZone: string;
  canManage: boolean;
}

/** `PUT /api/settings/time-zone` — treść zapisu (strefa IANA). */
export interface TenantTimeZoneSettingUpdate {
  timeZone: string;
}

/** `PUT /api/settings/time-zone` — odpowiedź: zapisana strefa. */
export interface TenantTimeZoneSettingUpdateResponse {
  timeZone: string;
}
